import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import Markdown from "@/components/Markdown";
import api from "@/lib/api";
import { Download, Share2, ArrowLeft, Layers, Loader2 } from "lucide-react";

const VARIANT_EMOJI = {
  struggling: "🌱",
  on_level:   "🎯",
  advanced:   "🚀",
  sen:        "🤲",
  esl:        "🌍",
};

const SLOT_LABELS = [
  ["explanation",   "Adjusted Explanation"],
  ["worksheet",     "Adjusted Worksheet"],
  ["quiz",          "Adjusted Quiz"],
  ["homework",      "Adjusted Homework"],
  ["teacher_notes", "Teacher Support Notes"],
  ["activities",    "Classroom Activity Suggestions"],
];

const VARIANT_KEYS = ["struggling", "on_level", "advanced", "sen", "esl"];

export default function DifferentiatedPack() {
  const { packId } = useParams();
  const [pack, setPack] = useState(null);
  const [activeVariant, setActiveVariant] = useState(0);
  const [notFound, setNotFound] = useState(false);

  // Background-task aware loader — polls until status === "ready"
  useEffect(() => {
    let alive = true;
    let timer = null;
    let attempts = 0;
    let announced = false;

    const tick = async () => {
      try {
        const { data } = await api.get(`/differentiated-pack/${packId}`);
        if (!alive) return;
        setPack(data);
        if (data.status === "ready") {
          if (!announced) {
            announced = true;
            const failed = (data.variants || []).filter((v) => !Object.keys(v.sections || {}).length).length;
            toast.success(`Differentiated Pack ready · ${data.coin_cost} coins used`,
              { description: failed ? `${failed} variant(s) refunded.` : "5 learner variants saved." });
          }
          return;
        }
        if (data.status === "failed") {
          toast.error(data.error || "Differentiation failed — coins refunded.");
          return;
        }
        attempts += 1;
        if (attempts < 90) timer = setTimeout(tick, 2000);
        else toast.error("This is taking longer than usual — please refresh in a minute.");
      } catch (e) {
        if (e?.response?.status === 404) { setNotFound(true); return; }
        attempts += 1;
        if (alive && attempts < 5) timer = setTimeout(tick, 3000);
      }
    };
    tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packId]);

  const exportPdf = () => {
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    window.open(`${BACKEND}/api/differentiated-pack/${packId}/pdf`, "_blank");
  };

  const sharePack = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: pack.title, url }); }
      catch (e) {
        // Swallow AbortError (user dismissed share sheet); report real errors.
        if (e?.name !== "AbortError") toast.error("Sharing failed");
      }
    } else {
      try { await navigator.clipboard.writeText(url); toast.success("Pack link copied"); }
      catch { toast.error("Could not copy link"); }
    }
  };

  if (notFound) {
    return (
      <Layout title="Differentiated Pack">
        <div className="max-w-md mx-auto text-center py-20" data-testid="pack-not-found">
          <p className="text-sm text-muted-foreground">This pack could not be found.</p>
          <Link to="/teach/kit" className="inline-block mt-4"><Button className="rounded-full">Back to kits</Button></Link>
        </div>
      </Layout>
    );
  }
  if (!pack) return <Layout title="Differentiated Pack"><PackGeneratingState /></Layout>;
  if (pack.status === "generating") return <Layout title={pack.title}><PackGeneratingState title={pack.title} /></Layout>;
  if (pack.status === "failed") {
    return (
      <Layout title={pack.title}>
        <div className="max-w-md mx-auto text-center py-20" data-testid="pack-failed">
          <p className="text-sm">{pack.error || "Differentiation failed — your coins were refunded."}</p>
          <Link to="/teach/kit" className="inline-block mt-4"><Button className="rounded-full">Back to kits</Button></Link>
        </div>
      </Layout>
    );
  }

  const v = pack.variants[activeVariant];

  return (
    <Layout title={pack.title}>
      <div className="max-w-6xl mx-auto" data-testid="diff-pack-view">
        {/* Hero band */}
        <div className="rounded-3xl border border-border bg-gradient-to-br from-emerald-50 via-white to-teal-50 dark:from-emerald-500/10 dark:via-card dark:to-teal-500/10 p-6 sm:p-8">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold">
            <Link to="/teach/kit" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              <ArrowLeft className="h-3 w-3" /> Back to kits
            </Link>
            <span className="text-muted-foreground">·</span>
            <span className="tb-chip-mint rounded-full px-2 py-0.5">
              <Layers className="inline h-3 w-3 mr-1" /> Differentiated Pack
            </span>
          </div>
          <h1 className="mt-3 font-display text-2xl sm:text-3xl tracking-tight font-medium">{pack.title}</h1>
          <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
            {pack.subject && <span className="rounded-full bg-muted px-2 py-0.5">{pack.subject}</span>}
            {pack.grade && <span className="rounded-full bg-muted px-2 py-0.5">Grade {pack.grade}</span>}
            {pack.language && <span className="rounded-full bg-muted px-2 py-0.5">{pack.language}</span>}
            <span className="rounded-full bg-muted px-2 py-0.5">{pack.coin_cost} coins used</span>
            {pack.refunded > 0 && (
              <span className="rounded-full bg-amber-100 dark:bg-amber-500/15 text-amber-900 dark:text-amber-200 px-2 py-0.5">
                +{pack.refunded} refunded
              </span>
            )}
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button onClick={exportPdf} variant="outline" className="rounded-full" data-testid="pack-export-pdf">
              <Download className="h-3.5 w-3.5 mr-1.5" /> Export PDF
            </Button>
            <Button onClick={sharePack} variant="outline" className="rounded-full" data-testid="pack-share">
              <Share2 className="h-3.5 w-3.5 mr-1.5" /> Share
            </Button>
          </div>
        </div>

        {/* Variant tabs */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="pack-variant-tabs">
          {pack.variants.map((vv, i) => {
            const active = activeVariant === i;
            const failed = !Object.keys(vv.sections || {}).length;
            return (
              <button
                key={vv.variant_key}
                onClick={() => setActiveVariant(i)}
                disabled={failed}
                className={`rounded-2xl border p-3 text-left transition-colors ${active
                  ? "bg-emerald-600 text-white border-emerald-600 shadow-lg"
                  : failed
                    ? "bg-muted text-muted-foreground border-border cursor-not-allowed opacity-50"
                    : "bg-card border-border hover:border-emerald-400/60"}`}
                data-testid={`pack-variant-${vv.variant_key}`}
              >
                <div className="text-lg">{VARIANT_EMOJI[vv.variant_key] || "✦"}</div>
                <div className="mt-1 text-[12px] font-medium leading-tight">{vv.label}</div>
                {failed && <div className="mt-1 text-[10px]">(refunded)</div>}
              </button>
            );
          })}
        </div>

        {/* Active variant content */}
        <div className="mt-6 space-y-5">
          {SLOT_LABELS.map(([k, label]) => {
            const content = v?.sections?.[k];
            if (!content) return null;
            return (
              <div key={k} className="rounded-2xl border border-border bg-card p-6" data-testid={`pack-slot-${k}`}>
                <h3 className="font-display text-lg tracking-tight font-medium mb-3">{label}</h3>
                <Markdown>{content}</Markdown>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}

function PackGeneratingState({ title }) {
  return (
    <div className="max-w-3xl mx-auto" data-testid="pack-generating">
      <div className="relative rounded-3xl border border-border bg-gradient-to-br from-emerald-50 via-white to-teal-50 dark:from-emerald-500/10 dark:via-card dark:to-teal-500/10 p-6 sm:p-9 overflow-hidden">
        <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-gradient-to-br from-emerald-400/30 to-teal-400/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <span className="tb-chip-mint text-[10px] uppercase tracking-[0.22em] font-semibold rounded-full px-2.5 py-0.5">
            <Layers className="inline h-3 w-3 mr-1" /> Crafting variants
          </span>
          <h1 className="mt-3 font-display text-2xl sm:text-3xl tracking-tight font-medium">
            Adapting {title ? <span className="text-emerald-700 dark:text-emerald-300">{title}</span> : "your lesson"} for 5 learner profiles…
          </h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl">
            TeeBee Intelligence is generating Struggling · On-Level · Advanced · SEN · ESL variants in parallel.
            Typical time 45 to 120 seconds — leave this page open or come back later.
          </p>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
        {VARIANT_KEYS.map((k) => (
          <div key={k} className="flex items-center gap-2 rounded-xl border border-border bg-card p-3 text-xs text-muted-foreground" data-testid={`pack-pending-${k}`}>
            <Loader2 className="h-3.5 w-3.5 animate-spin opacity-60" />
            <span className="capitalize">{VARIANT_EMOJI[k]} {k.replace("_", " ")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
